import { safeRandomUUID } from '@/core/types/ids';

export type AlarmSeverity = 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';
export type AlarmState = 'ACTIVE' | 'NORMAL';

export interface Alarm {
  id: string; // Unique UUID
  sourceId: string; // Device ID
  code: string; // E.g. 'WIRE_BREAK', 'OVERLOAD'
  title: string;
  description: string;
  severity: AlarmSeverity;
  state: AlarmState;
  firstOccurrenceTime: number; // Simulation time
  lastStateChangeTime: number;
  acknowledged: boolean;
  ackTime?: number;
  value?: number;
  threshold?: number;
}

export type EventType = 'ALARM_ACTIVE' | 'ALARM_NORMAL' | 'ALARM_ACK' | 'SIM_START' | 'SIM_PAUSE' | 'SIM_RESET' | 'PLC_RUN' | 'PLC_STOP' | 'SYSTEM' | 'IO_MANUAL';

export interface LogEvent {
  id: string;
  simTime: number;
  type: EventType;
  source: string;
  description: string;
}

export class AlarmManager {
  private activeAlarms = new Map<string, Alarm>();
  private allAlarms = new Map<string, Alarm>(); // By ID
  private events: LogEvent[] = [];
  private readonly MAX_EVENTS = 1000;
  private readonly MAX_ALARMS = 500;

  private generateAlarmKey(sourceId: string, code: string) {
    return `${sourceId}::${code}`;
  }

  // Called by Virtual Devices / Engine every tick
  evaluateCondition(
    condition: boolean,
    sourceId: string,
    code: string,
    title: string,
    description: string,
    severity: AlarmSeverity,
    simTime: number,
    value?: number,
    threshold?: number
  ) {
    const key = this.generateAlarmKey(sourceId, code);
    const existing = this.activeAlarms.get(key);

    if (condition) {
      if (existing) {
        // Update value if needed, but it's already active
        if (value !== undefined) existing.value = value;
      } else {
        // New alarm
        const newAlarm: Alarm = {
          id: safeRandomUUID(),
          sourceId,
          code,
          title,
          description,
          severity,
          state: 'ACTIVE',
          firstOccurrenceTime: simTime,
          lastStateChangeTime: simTime,
          acknowledged: false
        };
        if (value !== undefined) newAlarm.value = value;
        if (threshold !== undefined) newAlarm.threshold = threshold;
        this.activeAlarms.set(key, newAlarm);
        this.allAlarms.set(newAlarm.id, newAlarm);
        
        // Enforce max alarms limit by removing oldest NORMAL & ACKED alarms
        if (this.allAlarms.size > this.MAX_ALARMS) {
          this.cleanupOldAlarms();
        }

        this.logEvent(simTime, 'ALARM_ACTIVE', sourceId, `${title} - ${description}`);
      }
    } else {
      // Condition is false, clear it if it was active
      if (existing) {
        existing.state = 'NORMAL';
        existing.lastStateChangeTime = simTime;
        this.activeAlarms.delete(key);
        this.logEvent(simTime, 'ALARM_NORMAL', sourceId, `Cleared: ${title}`);
      }
    }
  }

  acknowledgeAlarm(alarmId: string, simTime: number) {
    const alarm = this.allAlarms.get(alarmId);
    if (alarm && !alarm.acknowledged) {
      alarm.acknowledged = true;
      alarm.ackTime = simTime;
      this.logEvent(simTime, 'ALARM_ACK', alarm.sourceId, `Acknowledged: ${alarm.title}`);
    }
  }

  logEvent(simTime: number, type: EventType, source: string, description: string) {
    this.events.unshift({
      id: safeRandomUUID(),
      simTime,
      type,
      source,
      description
    });
    if (this.events.length > this.MAX_EVENTS) {
      this.events.pop();
    }
  }

  getAlarms(): Alarm[] {
    return Array.from(this.allAlarms.values()).sort((a, b) => b.lastStateChangeTime - a.lastStateChangeTime);
  }

  getActiveAlarms(): Alarm[] {
    return Array.from(this.activeAlarms.values()).sort((a, b) => b.firstOccurrenceTime - a.firstOccurrenceTime);
  }

  getEvents(): LogEvent[] {
    return this.events;
  }

  reset() {
    this.activeAlarms.clear();
    this.allAlarms.clear();
    this.events = [];
    this.logEvent(0, 'SIM_RESET', 'System', 'Alarm Manager Reset');
  }

  private cleanupOldAlarms() {
    // Keep active alarms and unacknowledged alarms. Delete the oldest normal & acked.
    const removable = Array.from(this.allAlarms.values()).filter(a => a.state === 'NORMAL' && a.acknowledged);
    removable.sort((a, b) => a.firstOccurrenceTime - b.firstOccurrenceTime); // Oldest first
    
    let toRemove = this.allAlarms.size - this.MAX_ALARMS;
    for (const r of removable) {
      if (toRemove <= 0) break;
      this.allAlarms.delete(r.id);
      toRemove--;
    }
  }
}
